import { z } from 'zod';

/** Mirrors `AdminDto` on the backend. */
export interface AdminUser {
  id: string;
  email: string;
  name: string;
  role: string;
}

/** Mirrors `SessionDto` on the backend. */
export interface Session {
  admin: AdminUser;
  accessToken: string;
  expiresAt: string;
  expiresIn: number;
}

/**
 * The same rules the backend enforces, so the form can reject bad input before
 * a round trip. The server remains the authority — this is purely for feedback.
 */
export const loginSchema = z.object({
  email: z
    .string()
    .trim()
    .min(1, 'Email is required.')
    .email('Enter a valid email address.'),
  password: z.string().min(1, 'Password is required.'),
});

export type LoginCredentials = z.infer<typeof loginSchema>;

/** Matches the server's minimum, so the form can reject early. */
export const MIN_PASSWORD_LENGTH = 8;

export const requestResetSchema = z.object({
  email: z.string().trim().min(1, 'Email is required.').email('Enter a valid email address.'),
});

export const verifyCodeSchema = z.object({
  code: z
    .string()
    .trim()
    .transform((value) => value.replace(/[\s-]/g, ''))
    .pipe(z.string().regex(/^\d{6}$/, 'Enter the 6-digit code from your email.')),
});

export const newPasswordSchema = z
  .object({
    password: z
      .string()
      .min(MIN_PASSWORD_LENGTH, `Use at least ${MIN_PASSWORD_LENGTH} characters.`),
    confirmPassword: z.string(),
  })
  .refine((values) => values.password === values.confirmPassword, {
    message: 'The passwords do not match.',
    path: ['confirmPassword'],
  });

export type RequestResetForm = z.infer<typeof requestResetSchema>;
export type VerifyCodeForm = z.infer<typeof verifyCodeSchema>;
export type NewPasswordForm = z.infer<typeof newPasswordSchema>;
